import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import "./Footer.css";

/* ─── SVG social icons (no icon library needed) ───────────── */
function IconFacebook() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
    </svg>
  );
}

function IconInstagram() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
      <circle cx="12" cy="12" r="4"/>
      <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none"/>
    </svg>
  );
}

function IconTwitter() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M4 4l4.5 6L4 20h2.5l3.5-5.5L14 20h6l-4.8-6.3L19.5 4H17l-3.2 5L9 4H4z"/>
    </svg>
  );
}

const socials = [
  { label: "Facebook",  href: "https://facebook.com/glossy_lux_hair",  Icon: IconFacebook  },
  { label: "Instagram", href: "https://instagram.com/glossy_lux_hair", Icon: IconInstagram },
  { label: "Twitter",   href: "https://twitter.com/glossy_lux_hair",   Icon: IconTwitter   },
];

const links = [
  { label: "Shop",     to: "/shop"     },
  { label: "About",    to: "/#about"   },
  { label: "Contact",  to: "/#contact" },
];

const legal = [
  { label: "Privacy Policy",   to: "/privacy-policy"   },
  { label: "Terms of Service", to: "/terms-of-service" },
];

/* ─── Component ───────────────────────────────────────────── */
const Footer = () => {
  const ref    = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });

  return (
    <motion.footer
      ref={ref}
      className="footer"
      initial={{ opacity: 0, y: 24 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      role="contentinfo"
    >
      {/* ── Top decorative bar ── */}
      <div className="footer-bar" aria-hidden="true" />

      <div className="footer-inner">

        {/* ── Brand column ── */}
        <div className="footer-brand">
          <Link to="/" className="footer-logo" aria-label="GlossyLuxHair home">
            GlossyLux<em>Hair</em>
          </Link>
          <p className="footer-tagline">
            Luxury wigs crafted for confidence and beauty.
          </p>

          {/* Socials */}
          <div className="footer-socials" role="list">
            {socials.map(({ label, href, Icon }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="footer-social"
                aria-label={label}
                role="listitem"
              >
                <Icon />
              </a>
            ))}
          </div>
        </div>

        {/* ── Quick links ── */}
        <nav className="footer-nav" aria-label="Footer navigation">
          <span className="footer-nav-title">Quick links</span>
          <ul className="footer-nav-list">
            {links.map(({ label, to }) => (
              <li key={label}>
                <Link to={to} className="footer-nav-link">{label}</Link>
              </li>
            ))}
          </ul>
        </nav>

        {/* ── Contact ── */}
        <address className="footer-contact">
          <span className="footer-nav-title">Find us</span>
          <ul className="footer-contact-list">
            <li className="footer-contact-item">
              <span className="footer-contact-icon" aria-hidden="true">◎</span>
              <span>Winneba, North Campus, Ghana</span>
            </li>
            <li className="footer-contact-item">
              <span className="footer-contact-icon" aria-hidden="true">◁</span>
              <a href="tel:+233530557187" className="footer-nav-link">
                0530 557 187
              </a>
            </li>
          </ul>
        </address>

      </div>

      {/* ── Bottom bar ── */}
      <div className="footer-bottom">
        <p className="footer-copy">
          © {new Date().getFullYear()} GlossyLuxHair. All rights reserved.
        </p>
        <nav aria-label="Legal links">
          <ul className="footer-legal">
            {legal.map(({ label, to }) => (
              <li key={label}>
                <Link to={to} className="footer-legal-link">{label}</Link>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </motion.footer>
  );
};

export default Footer;